import { useCallback, useMemo } from 'react';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { resolveFloorGroupIdentifier } from '../../utils/roomFolders';
import { useAnnotationTree } from './useAnnotationContext';

/**
 * Resolve an annotation's `groupIdentifier` to its FLOOR group: sketches filed
 * in a room folder carry the folder's id, but floor-scoped consumers (export
 * elevations, IFC storeys) need the folder's parent floor. Ids that aren't
 * nested groups pass through unchanged. Hook wrapper around the canonical
 * resolveFloorGroupIdentifier (utils/roomFolders) — the resolution rule must
 * live in exactly one place.
 */
export function useResolveFloorGroup(): (groupIdentifier: string | null | undefined) => string | null | undefined {
  const annotations = useAnnotationTree();

  // Flatten the tree's group nodes (root + nested) into the flat shape the
  // canonical resolver takes.
  const groups = useMemo(() => {
    const result: { identifier: string; parentIdentifier?: string | null }[] = [];
    for (const item of annotations) {
      if (!isAnnotationContextGroup(item)) continue;
      result.push({ identifier: item.identifier, parentIdentifier: item.parentIdentifier ?? null });
      for (const child of item.annotations) {
        if (isAnnotationContextGroup(child)) result.push({ identifier: child.identifier, parentIdentifier: child.parentIdentifier ?? item.identifier });
      }
    }
    return result;
  }, [annotations]);

  return useCallback((groupIdentifier: string | null | undefined) => (groupIdentifier ? resolveFloorGroupIdentifier(groups, groupIdentifier) : groupIdentifier), [groups]);
}
